import { useState } from 'react'

import { useRequestTrack } from '@/api/hooks'
import type { Track } from '@/api/types'
import { usePendingPlayback } from '@/audio/pendingPlayback'
import { toQueueItem, usePlayer, type PlaySource } from '@/audio/store'
import { toast } from '@/components/ui'
import { describeError } from '@/features/search/describeError'
import type { ResultAction } from '@/features/search/SearchResultRow'

import { songKey } from './homeFeed'

type Song = Pick<Track, 'provider' | 'external_id' | 'title'>

export function useHomePlayback(source: PlaySource = 'unknown') {
  const request = useRequestTrack()
  const [busy, setBusy] = useState<Record<string, boolean>>({})
  const [errors, setErrors] = useState<Record<string, { message: string; action: ResultAction }>>({})

  function dismiss(key: string) {
    setErrors(({ [key]: _removed, ...rest }) => rest)
  }

  async function act(song: Song, action: ResultAction = 'play') {
    const key = songKey(song)
    if (busy[key]) return
    setBusy((previous) => ({ ...previous, [key]: true }))
    dismiss(key)
    try {
      // Re-resolve history entries: temporary files may have expired since the listen.
      const response = await request.mutateAsync({
        provider: song.provider,
        external_id: song.external_id,
        intent: action === 'download' ? 'save' : 'play',
      })
      if (action === 'download') {
        toast.success('Added to library', song.title)
        return
      }
      const item = toQueueItem(response.library_item)
      if (!item) {
        usePendingPlayback.getState().requestWhenReady(response.library_item.id, action, source)
        toast.info(`Preparing ${song.title}…`, action === 'play'
          ? 'It will play when ready.' : 'It will join your queue when ready.')
        return
      }
      const player = usePlayer.getState()
      if (action === 'play') player.playQueue([item], 0, { source })
      else if (action === 'next') {
        player.playNextUp([item])
        toast.success('Playing next', song.title)
      } else {
        player.enqueue([item])
        toast.success('Added to queue', song.title)
      }
    } catch (error) {
      setErrors((previous) => ({ ...previous, [key]: {
        message: describeError(error, 'Could not load this song. Try again.'), action,
      } }))
    } finally {
      setBusy((previous) => ({ ...previous, [key]: false }))
    }
  }

  return { act, busy, errors, dismiss }
}
